import { api } from '../axios'
import type { ApiResponse, Teacher } from '../../../types'
import type { SeniorityFilters } from '../types'

const appendQueryParam = (params: URLSearchParams, key: string, value: string | number | undefined) => {
  if (value === undefined || value === null || value === '') {
    return
  }
  params.append(key, String(value))
}

const buildSeniorityQueryString = (filters?: SeniorityFilters, extras?: Record<string, string | number | undefined>) => {
  const params = new URLSearchParams()
  if (extras) {
    Object.entries(extras).forEach(([key, value]) => {
      appendQueryParam(params, key, value)
    })
  }
  if (filters) {
    appendQueryParam(params, 'designation', filters.designation)
    appendQueryParam(params, 'school_level', filters.school_level)
    appendQueryParam(params, 'appointment_type', filters.appointment_type)
    appendQueryParam(params, 'appointment_date_from', filters.appointment_date_from)
    appendQueryParam(params, 'appointment_date_to', filters.appointment_date_to)
    appendQueryParam(params, 'appointment_date', filters.appointment_date)
  }
  return params.toString()
}

const buildSeniorityUrl = (path: string, filters?: SeniorityFilters, extras?: Record<string, string | number | undefined>) => {
  const query = buildSeniorityQueryString(filters, extras)
  return query ? `${path}?${query}` : path
}

export const teachersApi = {
  /** Ultra-fast lightweight endpoint for Teacher Transfer Map */
  getMapData: () =>
    api.get<ApiResponse<Teacher[]>>('/teachers/map-data'),

  /** List teachers (active only by default). Pass includeNonActive=true to include non-active teachers. */
  getAll: (page = 1, limit = 10, options?: { includeNonActive?: boolean }) => {
    const params = new URLSearchParams()
    params.append('page', String(page))
    params.append('limit', String(limit))
    if (options?.includeNonActive !== true) {
      params.append('includeNonActive', 'false')
    }
    return api.get<ApiResponse & { pagination: any }>(`/teachers?${params.toString()}`)
  },

  getById: (id: number) =>
    api.get<ApiResponse<Teacher>>(`/teachers/${id}`),

  /** Preview the next auto-generated Teacher ID (e.g. "Edu-Tch-2026-00001"). */
  async previewNextTeacherId(): Promise<string> {
    const res = await api.get<ApiResponse<{ teacher_ID: string }>>('/teachers/next-id/preview')
    return res.data?.data?.teacher_ID ?? ''
  },

  /** Returns true if a teacher with this business ID already exists. */
  async teacherIdExists(teacherId: string): Promise<boolean> {
    try {
      const res = await api.get<ApiResponse<{ id: number; teacher_ID: string | null }>>(
        `/teachers/teacher-id/${encodeURIComponent(teacherId)}`
      )
      return Boolean(res.data.success && res.data.data)
    } catch (error: any) {
      if (error.response?.status === 404) return false
      throw error
    }
  },

  getByPhone: (phone: string) =>
    api.get<ApiResponse<Teacher>>(`/teachers/phone/${phone}`),

  /** Returns true if an active teacher with this phone number already exists. */
  async phoneExists(phone: string): Promise<boolean> {
    try {
      const res = await api.get<ApiResponse<{ id: number; phone_number: string | null }>>(
        `/teachers/phone/${encodeURIComponent(phone)}`
      )
      return Boolean(res.data.success && res.data.data)
    } catch (error: any) {
      if (error.response?.status === 404) return false
      throw error
    }
  },

  /** Returns true if an active teacher with this email already exists. */
  async emailExists(email: string): Promise<boolean> {
    try {
      const res = await api.get<ApiResponse<{ id: number; email: string | null }>>(
        `/teachers/email/${encodeURIComponent(email.trim())}`
      )
      return Boolean(res.data.success && res.data.data)
    } catch (error: any) {
      if (error.response?.status === 404) return false
      throw error
    }
  },

  create: (data: Teacher) =>
    api.post<ApiResponse<Teacher>>('/teachers', data),

  /** Minimal create for onboarding (admin-only). */
  createMinimal: (data: { teacher_name: string; teacher_ID: string; phone_number: string; school_id: string }) =>
    api.post<ApiResponse<Teacher>>('/teachers/minimal', data as any),

  /** Super-admin bulk minimal import from CSV (μ.tch.ingest). */
  importMinimalCsv: async (file: File, opts?: { dryRun?: boolean }) => {
    const form = new FormData()
    form.append('file', file)
    const q = opts?.dryRun ? '?dry_run=true' : ''
    return api.post(`/teachers/import/minimal-csv${q}`, form)
  },

  update: (id: number, data: Partial<Teacher>) =>
    api.put<ApiResponse<Teacher>>(`/teachers/${id}`, data),

  delete: (id: number) =>
    api.delete<ApiResponse>(`/teachers/${id}`),

  /** Request permanent (hard) delete. Reason required. Super Admin: executes immediately. Admin: creates request for Super Admin approval. idOrTeacherId can be numeric id or teacher_ID string. */
  requestPermanentDelete: (idOrTeacherId: number | string, reason: string) =>
    api.delete<ApiResponse>(`/teachers/${encodeURIComponent(String(idOrTeacherId))}/permanent`, { data: { reason } }),

  getBySchool: (schoolId: string, page?: number, limit?: number) => {
    const params = new URLSearchParams()
    if (page) params.append('page', page.toString())
    if (limit) params.append('limit', limit.toString())
    const query = params.toString()
    const url = query ? `/teachers/school/${schoolId}?${query}` : `/teachers/school/${schoolId}`
    return api.get<ApiResponse<{ teachers: Teacher[]; pagination?: any } | Teacher[]>>(url)
  },

  search: (query: string) =>
    api.get<ApiResponse<Teacher[]>>(`/teachers/search?q=${encodeURIComponent(query)}`),

  getStats: () =>
    api.get<ApiResponse>(`/teachers/stats`),

  getSeniorityList: (page = 1, limit = 20, filters?: SeniorityFilters) =>
    api.get<ApiResponse & { pagination: any }>(
      buildSeniorityUrl('/teachers/seniority', filters, { page, limit })
    ),

  exportSeniorityExcel: (filters?: SeniorityFilters) =>
    api.get<Blob>(buildSeniorityUrl('/teachers/seniority/export/excel', filters), { responseType: 'blob' }),

  exportSeniorityPdf: (filters?: SeniorityFilters) =>
    api.get<Blob>(buildSeniorityUrl('/teachers/seniority/export/pdf', filters), { responseType: 'blob' }),

  getNonActive: (type?: string, page = 1, limit = 50) => {
    const params = new URLSearchParams()
    if (type && type !== 'all') params.append('type', type)
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    return api.get<ApiResponse & { pagination: any }>(`/teachers/non-active?${params.toString()}`)
  },

  exportNonActive: (type?: string) => {
    const params = new URLSearchParams()
    if (type && type !== 'all') params.append('type', type)
    return api.get(`/teachers/non-active/export?${params.toString()}`, { responseType: 'arraybuffer' })
  },

  markNonActive: (id: number, data: { non_active_type: string; non_active_date: string }) =>
    api.patch<ApiResponse>(`/teachers/${id}/mark-non-active`, data),

  reactivate: (id: number) =>
    api.patch<ApiResponse>(`/teachers/${id}/reactivate`),

  getSuspensionHistory: (id: number) =>
    api.get<ApiResponse>(`/teachers/${id}/suspension-history`),

  getSuspensionCount: (id: number) =>
    api.get<ApiResponse<{ teacher_id: number; suspension_count: number }>>(`/teachers/${id}/suspension-count`),

  getActiveSuspended: (page = 1, limit = 50) => {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    return api.get<ApiResponse & { pagination: any }>(`/teachers/active-suspended?${params.toString()}`)
  },

  exportActiveSuspended: () => {
    return api.get(`/teachers/active-suspended/export`, { responseType: 'arraybuffer' })
  }
}

export { simpleProfileApi } from './simpleProfile'

export type { SeniorityFilters }

export type TeacherCsvImportRowResult = {
  row: number
  status: 'created' | 'skipped' | 'failed'
  teacher_name?: string
  teacher_ID?: string
  phone_number?: string
  school_id?: string
  message?: string
  teacher_db_id?: number
}

export type TeacherCsvImportSummary = {
  dryRun: boolean
  totalRows: number
  created: number
  skipped: number
  failed: number
  results: TeacherCsvImportRowResult[]
}

